import { Plus, X, ChevronDown } from "@/components/icons";
import Portal from "../../../components/Portal";
import SharedDatePicker from "../../../components/DatePicker";

// Re-export DatePicker for in-module usage
export const DatePicker = SharedDatePicker;

// -----------------------------------------------------------------------------
// Date helpers
// -----------------------------------------------------------------------------
export function calcAge(birthDate) {
  if (!birthDate) return "—";
  try {
    const b = new Date(birthDate);
    const now = new Date();
    let years = now.getFullYear() - b.getFullYear();
    let months = now.getMonth() - b.getMonth();
    if (now.getDate() < b.getDate()) months--;
    if (months < 0) {
      years--;
      months += 12;
    }
    return `${years}a ${months}m`;
  } catch {
    return "—";
  }
}

export const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Returns a clean PatientIn payload (only writable fields) so PUT /patients works.
export function sanitizePatient(p) {
  return {
    full_name: p.full_name || "",
    document_id: p.document_id || null,
    birth_date: p.birth_date || null,
    gender: p.gender || null,
    phone: p.phone || null,
    email: p.email || null,
    address: p.address || null,
    blood_type: p.blood_type || null,
    allergies: p.allergies || null,
    notes: p.notes || null,
    photo_url: p.photo_url || null,
    office_id: p.office_id || null,
    preferred_pharmacy_id: p.preferred_pharmacy_id || null,
  };
}

// -----------------------------------------------------------------------------
// UI primitives — Minimalist design system (white bg, black text, subtle borders)
// -----------------------------------------------------------------------------
export const Card = ({ children, className = "" }) => (
  <div className={`bg-[#F5F5F7] rounded-[24px] p-5 ${className}`}>
    {children}
  </div>
);

export const Empty = ({ children }) => (
  <div className="flex flex-col items-center justify-center py-10 text-black/40">
    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-2">
      <Plus className="w-4 h-4 text-black/30" strokeWidth={1.75} weight="regular" />
    </div>
    <div className="text-[12.5px]">{children}</div>
  </div>
);

export function KV({ label, value, big = false }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
        {label}
      </div>
      <div
        className={`mt-1 text-black font-semibold truncate ${big ? "text-[16px]" : "text-[13px]"}`}
      >
        {value}
      </div>
    </div>
  );
}

export function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white px-3 py-2.5 flex items-center justify-between gap-3 min-w-0">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
          {label}
        </div>
        <div className="text-[16px] font-semibold text-black truncate mt-0.5 tabular-nums">
          {value}
        </div>
      </div>
      <span className="w-8 h-8 rounded-xl bg-[#F5F5F7] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
      </span>
    </div>
  );
}

export function SectionCard({ icon: Icon, title, onAdd, rightSlot, children }) {
  return (
    <div className="bg-[#F5F5F7] rounded-[24px] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-black/60" strokeWidth={1.75} weight="regular" />}
          <div className="text-[13px] font-semibold text-black">{title}</div>
        </div>
        <div className="flex items-center gap-2">
          {rightSlot}
          {onAdd && (
            <button
              onClick={onAdd}
              data-testid={`add-${title.toLowerCase().replace(/\s/g, "-")}`}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold text-[#0071E3] hover:bg-[#0071E3]/5 transition"
              title={`Agregar ${title}`}
            >
              <Plus className="w-3 h-3" strokeWidth={2} weight="regular" />
              Añadir
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export function SectionHeader({ icon: Icon, title, onAdd }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-black/60" strokeWidth={1.75} weight="regular" />}
        <div className="text-[13px] font-semibold text-black">{title}</div>
      </div>
      {onAdd && (
        <button
          onClick={onAdd}
          data-testid={`section-add-${title.toLowerCase().replace(/\s/g, "-")}`}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold text-[#0071E3] hover:bg-[#0071E3]/5 transition"
          title={`Agregar ${title}`}
        >
          <Plus className="w-3 h-3" strokeWidth={2} weight="regular" />
          Añadir
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Form primitives
// -----------------------------------------------------------------------------
function Label({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1">
      {children}
    </div>
  );
}

export function Input({ label, type = "text", value, onChange, placeholder, required }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full h-10 px-3 rounded-xl border border-black/[0.08] bg-white text-[13px] text-black placeholder:text-black/30 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition"
      />
    </label>
  );
}

export function Textarea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 rounded-xl border border-black/[0.08] bg-white text-[13px] text-black placeholder:text-black/30 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition resize-none"
      />
    </label>
  );
}

export function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 px-3 pr-9 rounded-xl border border-black/[0.08] bg-white text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition appearance-none cursor-pointer"
        >
          {options.map((o) => (
            <option key={o.v} value={o.v}>
              {o.l}
            </option>
          ))}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-black/50 pointer-events-none" />
      </div>
    </label>
  );
}

/**
 * DatePicker — shared component with keyboard typing support (dd/mm/yyyy) + Calendar popover.
 * Re-exported from `/components/DatePicker.jsx` for use anywhere in the app.
 */

// -----------------------------------------------------------------------------
// Modal shell
// -----------------------------------------------------------------------------
export function Modal({ title, onClose, children }) {
  return (
    <Portal>
      <div
        className="fixed inset-0 z-[80] bg-black/15 backdrop-blur-md flex items-center justify-center p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          className="bg-white rounded-[24px] w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-black/[0.05]">
            <h3 className="text-[16px] font-semibold text-black tracking-tight">{title}</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition"
            >
              <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto no-scrollbar flex-1">{children}</div>
        </div>
      </div>
    </Portal>
  );
}
